import { useCallback, useMemo } from 'react';
import { SxProps, Theme } from '@mui/material';
import { parseFormattedText, formattedTextToAccessible, parseSpecialChars, TextSegment } from '../utils/formattedText';

interface UseFormattedTextProps {
  text: string;
  defaultSx?: SxProps<Theme>;
}

interface UseFormattedTextReturn {
  ///**
  // * Renders the formatted text as accessible React components
  // */
  //renderedText: JSX.Element;
  /**
   * The parsed text segments
   */
  segments: TextSegment[];
  /**
   * Plain text version with no formatting (for screen readers)
   */
  accessibleText: string;
  /**
   * Maps formatting styles to appropriate font styles
   */
  mapStyleToFont: (segment: TextSegment) => SxProps<Theme>;
}

/**
 * Hook to handle formatted text with <b>, <i>, <u> tags
 * Provides accessibility-friendly rendering and parsing
 */
export const useFormattedText = ({ text, defaultSx = {} }: UseFormattedTextProps): UseFormattedTextReturn => {
  // Parse text into segments with formatting information
  const segments = useMemo(() => parseFormattedText(text || ''), [text]);
  
  // Create accessible version for screen readers
  const accessibleText = useMemo(() => formattedTextToAccessible(text || ''), [text]);

  // Map segment formatting to MUI typography styles
  const mapStyleToFont = useCallback((segment: TextSegment): SxProps<Theme> => {
    return {
      fontWeight: segment.isBold ? 'bold' : 'inherit',
      fontStyle: segment.isItalic ? 'italic' : 'inherit',
      textDecoration: segment.isUnderline ? 'underline' : 'inherit',
      ...defaultSx
    };
  }, [defaultSx]);

  return {
    segments,
    accessibleText,
    mapStyleToFont
  };
};

export default useFormattedText;
